import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { z } from "zod";

import { ApiError, apiRequest, readCsrfCookie, throwFromResponse } from "@/lib/api";
import { newIdempotencyKey } from "@/lib/idempotency";
import {
  BrandCreateSchema,
  BrandPanelDefaultRequestSchema,
  BrandReadSchema,
  BrandSmtpConfigReadSchema,
  BrandSmtpConfigUpdateSchema,
  BrandSmtpTestResultSchema,
  BrandSmtpTestSchema,
  BrandUpdateSchema,
  CurrentBrandingResponseSchema,
  type BrandCreateInput,
  type BrandPanelDefaultInput,
  type BrandRead,
  type BrandsListFilters,
  type BrandSmtpConfigRead,
  type BrandSmtpConfigUpdateInput,
  type BrandSmtpTestInput,
  type BrandSmtpTestResult,
  type BrandUpdateInput,
  type CurrentBrandingResponse,
} from "@/lib/schemas/branding";

/**
 * Brand list response shape (Phase 6): `{items: [...], meta: {next_cursor, prev_cursor, total_estimate}}`.
 * Diverso da approvals (`data + next_cursor` flat) — mirror del Pydantic
 * `BrandListResponse`.
 */
const BrandListResponseSchema = z.object({
  items: z.array(BrandReadSchema),
  meta: z.object({
    next_cursor: z.string().nullable(),
    prev_cursor: z.string().nullable(),
    total_estimate: z.number().int().nullable().optional(),
  }),
});

export type BrandListResponse = z.infer<typeof BrandListResponseSchema>;

/**
 * Async task accepted envelope (Phase 2 §05): 202 ritornato dagli endpoint
 * che enqueue-ano work al worker (es. asset upload + ACME issuance).
 * Mirror del Pydantic `AsyncTaskAccepted` in `dpanel_schemas.common`.
 */
const AsyncTaskAcceptedSchema = z.object({
  task_id: z.guid(),
  task_kind: z.string(),
  status_url: z.string(),
  estimated_completion_seconds: z.number().int().nullable().optional(),
  created_at: z.string(),
  result_url: z.string().nullable().optional(),
});

export type AsyncTaskAccepted = z.infer<typeof AsyncTaskAcceptedSchema>;

function buildQS(f: BrandsListFilters): string {
  const p = new URLSearchParams();
  if (f.owner_id) p.set("owner_id", f.owner_id);
  if (f.status) p.set("status", f.status);
  if (f.cursor) p.set("cursor", f.cursor);
  p.set("limit", String(f.limit));
  return p.toString();
}

/**
 * GET /branding/current — host-aware brand resolution (Phase 10B).
 *
 * Backend returns the brand matching the current `Host` header (or the
 * DPanel default if none matches). Used da `BrandingThemeProvider` per
 * applicare le CSS custom properties al chrome del panel a runtime.
 *
 * Cache: 5min (server sets `Cache-Control: max-age=300`). Anti-enum-leak:
 * backend ritorna sempre 200 (default theme su host sconosciuto) — vedi
 * ADR-0030.
 */
export function useCurrentBranding(): UseQueryResult<CurrentBrandingResponse> {
  return useQuery({
    queryKey: ["branding", "current"],
    queryFn: async () => {
      const raw = await apiRequest<unknown>("/branding/current");
      return CurrentBrandingResponseSchema.parse(raw);
    },
    staleTime: 300_000, // 5 min, matches backend Cache-Control
    refetchOnWindowFocus: false,
  });
}

/**
 * GET /branding/effective — session-aware brand resolution (backlog 3.7).
 *
 * Precedence: the user's `app_users.brand_id` override, else the host-based
 * resolution (same as /branding/current). Enabled only when authenticated;
 * `BrandingThemeProvider` applies it over the host-based brand so a delegated
 * reseller logging into the main panel sees their own brand.
 */
export function useEffectiveBranding(enabled: boolean): UseQueryResult<CurrentBrandingResponse> {
  return useQuery({
    queryKey: ["branding", "effective"],
    queryFn: async () => {
      const raw = await apiRequest<unknown>("/branding/effective");
      return CurrentBrandingResponseSchema.parse(raw);
    },
    enabled,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useBrandsList(
  filters: BrandsListFilters,
): UseQueryResult<BrandListResponse> {
  return useQuery({
    queryKey: ["brands", "list", filters],
    queryFn: async () => {
      const raw = await apiRequest<unknown>(`/brands?${buildQS(filters)}`);
      return BrandListResponseSchema.parse(raw);
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useBrandDetail(id: string | undefined): UseQueryResult<BrandRead> {
  return useQuery({
    queryKey: ["brands", "detail", id],
    queryFn: async () => {
      const raw = await apiRequest<unknown>(`/brands/${id}`);
      return BrandReadSchema.parse(raw);
    },
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useBrandCreate(): UseMutationResult<BrandRead, Error, BrandCreateInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandCreateInput) => {
      const validated = BrandCreateSchema.parse(input);
      const raw = await apiRequest<unknown>("/brands", {
        method: "POST",
        body: validated,
        idempotencyKey: newIdempotencyKey(),
      });
      return BrandReadSchema.parse(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

/**
 * PATCH /brands/{id}/panel-default — superuser-only toggle (Phase 11.7).
 *
 * Setta `is_panel_default=true|false` su un brand. Quando true, il backend
 * azzera lo stesso flag su qualsiasi altro brand (DB partial unique index
 * `ix_app_brands_panel_default` enforce single panel default).
 *
 * Audit emit `brand.panel_default_set` (warning severity) BE-side.
 *
 * Onsuccess: invalidate `["brands"]` cosi' UI rifresca subito; anche
 * `["branding", "current"]` perche' il resolver cambia decisione quando
 * il flag flips.
 */
export function useBrandPanelDefault(
  brandId: string | undefined,
): UseMutationResult<BrandRead, Error, BrandPanelDefaultInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandPanelDefaultInput) => {
      if (!brandId) throw new Error("brandId is required");
      const validated = BrandPanelDefaultRequestSchema.parse(input);
      const raw = await apiRequest<unknown>(`/brands/${brandId}/panel-default`, {
        method: "PATCH",
        body: validated,
      });
      return BrandReadSchema.parse(raw);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["brands"] });
      void queryClient.invalidateQueries({ queryKey: ["brands", "detail", data.id] });
      void queryClient.invalidateQueries({ queryKey: ["branding", "current"] });
    },
  });
}

/**
 * Outcome di DELETE /brands/{id} (approval-gated `brand.delete`).
 *
 * BE risponde 202 + DeleteAcknowledgement. Se `decision_required=true` il
 * caller dovrebbe mostrare un link "in attesa di approvazione" su
 * `/approvals/{approval_id}` invece di considerare l'azione fatta. Mirror
 * di UserDeleteResult.
 */
export interface BrandDeleteResult {
  executed: boolean;
  decision_required: boolean;
  approval_id?: string;
  decision_url?: string;
}

const BrandDeleteAckSchema = z
  .object({
    executed: z.boolean().optional(),
    decision_required: z.boolean().optional(),
    approval_id: z.guid().optional(),
    decision_url: z.string().optional(),
  })
  .passthrough();

/**
 * DELETE /brands/{id} — approval-gated `brand.delete`.
 *
 * BE-side gates:
 *  - require_mfa_on_writes (MFA step-up; FE intercetta via MfaVerifyDialog)
 *  - approval_gate("brand.delete") → 202 DeleteAcknowledgement
 *  - has_dependents check: 409 resource_in_use se esistono `app_brand_domains`
 *    legati (il caller deve rimuoverli prima)
 *
 * onSuccess invalida `brands` list + branding/current (host resolver puo'
 * cambiare se eliminato un brand panel-default).
 */
export function useBrandDelete(): UseMutationResult<BrandDeleteResult, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const raw = await apiRequest<unknown>(`/brands/${id}`, {
        method: "DELETE",
        idempotencyKey: newIdempotencyKey(),
      });
      const parsed = BrandDeleteAckSchema.parse(raw ?? {});
      return {
        executed: parsed.executed ?? false,
        decision_required: parsed.decision_required ?? false,
        approval_id: parsed.approval_id,
        decision_url: parsed.decision_url,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["brands"] });
      void queryClient.invalidateQueries({ queryKey: ["branding", "current"] });
      void queryClient.invalidateQueries({ queryKey: ["approvals"] });
    },
  });
}

export function useBrandUpdate(
  brandId: string | undefined,
): UseMutationResult<BrandRead, Error, BrandUpdateInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandUpdateInput) => {
      if (!brandId) throw new Error("brandId is required");
      const validated = BrandUpdateSchema.parse(input);
      const raw = await apiRequest<unknown>(`/brands/${brandId}`, {
        method: "PATCH",
        body: validated,
      });
      return BrandReadSchema.parse(raw);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["brands"] });
      void queryClient.invalidateQueries({ queryKey: ["brands", "detail", data.id] });
    },
  });
}

/* ────────────────────────────────────────────── Asset upload (multipart) */

export interface AssetUploadInput {
  asset_kind: "logo" | "favicon" | "login_background" | "email_header";
  file: File;
  alt_text?: string | undefined;
}

/**
 * POST /brands/{id}/assets — multipart/form-data.
 * Server-side: magic-byte check + Pillow re-encode + max 5MB, max 2048×2048 px
 * (CLAUDE.md §8). SVG vietato a livello mime whitelist.
 *
 * `apiRequest` non gestisce multipart nativamente per body=object; usiamo fetch
 * diretto con FormData per evitare di stringfy.
 */
export function useBrandAssetUpload(
  brandId: string | undefined,
): UseMutationResult<AsyncTaskAccepted, Error, AssetUploadInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssetUploadInput) => {
      if (!brandId) throw new Error("brandId is required");
      const fd = new FormData();
      fd.append(
        "metadata",
        JSON.stringify({
          asset_kind: input.asset_kind,
          ...(input.alt_text ? { alt_text: input.alt_text } : {}),
        }),
      );
      fd.append("file", input.file);

      // CSRF double-submit: il backend (Phase 10A) richiede X-CSRF-Token su
      // tutte le mutation. `apiRequest` lo inietta in automatico, ma qui
      // usiamo fetch raw per FormData (NON usare apiRequest che farebbe
      // JSON.stringify del File). Mintiamo l'header manualmente.
      // NB: niente Content-Type esplicito — il browser setta multipart con
      // boundary corretto solo se non lo forziamo.
      const headers: Record<string, string> = {
        "Idempotency-Key": newIdempotencyKey(),
      };
      const csrfToken = readCsrfCookie();
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      }
      const res = await fetch(`/api/v1/brands/${brandId}/assets`, {
        method: "POST",
        credentials: "include",
        headers,
        body: fd,
      });
      if (!res.ok) {
        // Symmetric to apiRequest: parse problem+json, 401 → AuthRequiredError,
        // 403 mfa_required → opens global MfaVerifyDialog, others → ApiError.
        // AssetUploadPanel uses formatApiError() to render the i18n hint.
        await throwFromResponse(res);
      }
      // Backend ritorna 202 + AsyncTaskAccepted (Phase 2 §05), NON BrandRead:
      // il worker re-encoda il file (Pillow) e aggiorna app_brand_assets in
      // async. La FE invalida le query brand cosi' al prossimo refetch si
      // popolano gli `asset_urls` aggiornati.
      const raw: unknown = await res.json();
      return AsyncTaskAcceptedSchema.parse(raw);
    },
    onSuccess: () => {
      // Invalidate brand list + detail per re-fetch dopo che il worker
      // termina (latency tipica: < 2s per Pillow re-encode di un PNG 5MB).
      // Workaround Phase 10F: niente polling explicit del task_id; ci
      // affidiamo alla refetch automatica. Phase 11+ aggiunge polling
      // `/tasks/{id}` per gestire failure visualmente.
      void queryClient.invalidateQueries({ queryKey: ["brands"] });
      if (brandId) {
        void queryClient.invalidateQueries({ queryKey: ["brands", "detail", brandId] });
      }
      // Also refresh the APPLIED branding (host/effective) so the live panel
      // favicon/logo (BrandingThemeProvider) updates without navigating away.
      void queryClient.invalidateQueries({ queryKey: ["branding"] });
    },
  });
}

/* ────────────────────────────────────────────── SMTP config (ADR-0087) */

/**
 * GET /brands/{id}/smtp-config — masked read.
 *
 * Il backend ritorna 404 quando il brand non ha ancora una config SMTP. Qui
 * lo trattiamo come stato "non configurato" (`data === null`) invece che come
 * errore, così la card può mostrare l'empty-state senza un box rosso.
 * Admin/superuser only (gate BE 403 per reseller).
 */
export function useBrandSmtpConfig(
  brandId: string | undefined,
): UseQueryResult<BrandSmtpConfigRead | null> {
  return useQuery({
    queryKey: ["brands", "smtp-config", brandId],
    queryFn: async () => {
      try {
        const raw = await apiRequest<unknown>(`/brands/${brandId}/smtp-config`);
        return BrandSmtpConfigReadSchema.parse(raw);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null; // not configured yet — empty state, not an error
        }
        throw err;
      }
    },
    enabled: Boolean(brandId),
    staleTime: 30_000,
  });
}

/**
 * PATCH /brands/{id}/smtp-config — synchronous config update (ADR-0087).
 *
 * Non più approval-gated: l'edit di una config SMTP è una modifica di
 * configurazione, applicata subito con MFA step-up (require_mfa_on_writes BE)
 * + audit. La password (se presente) finisce in OpenBao KV server-side, mai
 * in DB. Idempotency-Key client-side per dedup retry.
 */
export function useUpdateBrandSmtpConfig(
  brandId: string | undefined,
): UseMutationResult<BrandSmtpConfigRead, Error, BrandSmtpConfigUpdateInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandSmtpConfigUpdateInput) => {
      if (!brandId) throw new Error("brandId is required");
      const validated = BrandSmtpConfigUpdateSchema.parse(input);
      const raw = await apiRequest<unknown>(`/brands/${brandId}/smtp-config`, {
        method: "PATCH",
        body: validated,
        idempotencyKey: newIdempotencyKey(),
      });
      return BrandSmtpConfigReadSchema.parse(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["brands", "smtp-config", brandId] });
      void queryClient.invalidateQueries({ queryKey: ["brands", "detail", brandId] });
    },
  });
}

/**
 * POST /brands/{id}/smtp-config/test — send a test email (ADR-0087).
 *
 * Synchronous: il backend tenta la connessione/auth/invio usando la config
 * SALVATA + la password da OpenBao KV e ritorna l'esito classificato
 * (success/connection_failed/authentication_failed/tls_failed) + detail.
 * Diagnostico, non muta stato di dominio (aggiorna solo last_test_*), quindi
 * niente Idempotency-Key. Invalida la query smtp-config per riflettere il
 * nuovo `last_test_outcome` se la card lo mostra.
 */
export function useTestBrandSmtp(
  brandId: string | undefined,
): UseMutationResult<BrandSmtpTestResult, Error, BrandSmtpTestInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: BrandSmtpTestInput) => {
      if (!brandId) throw new Error("brandId is required");
      const validated = BrandSmtpTestSchema.parse(input);
      const raw = await apiRequest<unknown>(`/brands/${brandId}/smtp-config/test`, {
        method: "POST",
        body: validated,
      });
      return BrandSmtpTestResultSchema.parse(raw);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["brands", "smtp-config", brandId] });
    },
  });
}
